/**
 * PdfPageRenderer Component
 * Renders all pages of a PDF document as canvas-based images.
 * Unlike <iframe>, these render into the DOM and are visible in print/PDF export.
 */

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import * as pdfjsLib from "pdfjs-dist";

// Use the bundled worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url
).toString();

const PdfPageRenderer = ({ src, scale = 1.5, className = "" }) => {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const renderTasksRef = useRef([]);

  useEffect(() => {
    if (!src) return;

    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    // Clear previous content
    container.innerHTML = "";
    setError(null);
    setLoading(true);
    setNumPages(0);
    renderTasksRef.current = [];

    const loadPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: src,
          cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.4.168/cmaps/",
          cMapPacked: true,
        });

        const pdf = await loadingTask.promise;
        if (cancelled) return;

        setNumPages(pdf.numPages);

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) break;

          const page = await pdf.getPage(pageNum);
          if (cancelled) break;

          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.className = "pdf-page-canvas";
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          canvas.style.display = "block";
          canvas.style.marginBottom = pageNum < pdf.numPages ? "8px" : "0";

          container.appendChild(canvas);

          const ctx = canvas.getContext("2d");
          const renderTask = page.render({
            canvasContext: ctx,
            viewport,
          });

          renderTasksRef.current.push(renderTask);

          await renderTask.promise;
        }

        if (!cancelled) setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error("PdfPageRenderer: Error loading PDF:", err);
          setError(err.message || "Impossible de charger le PDF");
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      renderTasksRef.current.forEach((task) => {
        try { task.cancel(); } catch {}
      });
      renderTasksRef.current = [];
    };
  }, [src, scale]);

  return (
    <div className={`pdf-page-renderer ${className}`} ref={containerRef}>
      {loading && !error && (
        <div className="pdf-page-renderer__loading">
          Chargement du PDF...
        </div>
      )}
      {error && (
        <div className="pdf-page-renderer__error">
          Erreur: {error}
        </div>
      )}
    </div>
  );
};

PdfPageRenderer.propTypes = {
  src: PropTypes.string.isRequired,
  scale: PropTypes.number,
  className: PropTypes.string,
};

export default PdfPageRenderer;
